/**
 * MCP Resource for Power Automate Environments
 *
 * Exposes environment information as MCP resources.
 */

import type { Resource, ResourceTemplate } from "@modelcontextprotocol/sdk/types.js";
import type { FlowManagementApi } from "../api/flow-management.js";
import type { Environment } from "../api/types.js";

// ============================================================================
// Types
// ============================================================================

export interface EnvironmentResourceContext {
  api: FlowManagementApi;
  defaultEnvironmentId: string;
}

// ============================================================================
// Resource Templates
// ============================================================================

export const environmentResourceTemplates: ResourceTemplate[] = [
  {
    uriTemplate: "powerautomate://environments",
    name: "Environments",
    description: "List all accessible Power Automate environments",
    mimeType: "application/json",
  },
  {
    uriTemplate: "powerautomate://environments/{environmentId}",
    name: "Environment Details",
    description: "Get details for a specific environment",
    mimeType: "application/json",
  },
  {
    uriTemplate: "powerautomate://environments/{environmentId}/summary",
    name: "Environment Summary",
    description: "Get a summary of flows and connections in an environment",
    mimeType: "application/json",
  },
];

// ============================================================================
// Resource Listing
// ============================================================================

/**
 * List all environment resources
 */
// eslint-disable-next-line @typescript-eslint/require-await -- Conforms to the resource-lister signature, which is uniformly async.
export async function listEnvironmentResources(
  context: EnvironmentResourceContext
): Promise<Resource[]> {
  const resources: Resource[] = [];

  // Add main environments list
  resources.push({
    uri: "powerautomate://environments",
    name: "All Environments",
    description: "List of all accessible Power Automate environments",
    mimeType: "application/json",
  });

  // Add default environment
  resources.push({
    uri: `powerautomate://environments/${context.defaultEnvironmentId}`,
    name: "Default Environment",
    description: "Currently configured default environment",
    mimeType: "application/json",
  });

  resources.push({
    uri: `powerautomate://environments/${context.defaultEnvironmentId}/summary`,
    name: "Default Environment Summary",
    description: "Flow and connection summary for the default environment",
    mimeType: "application/json",
  });

  return resources;
}

// ============================================================================
// Resource Reading
// ============================================================================

/**
 * Read an environment resource
 */
export async function readEnvironmentResource(
  context: EnvironmentResourceContext,
  uri: string
): Promise<{ contents: Array<{ uri: string; mimeType: string; text: string }> } | null> {
  const parsed = parseEnvironmentUri(uri);
  if (!parsed) return null;

  let content: unknown;

  switch (parsed.type) {
    case "list": {
      // Get environments from API
      const environments = await context.api.listEnvironments();
      content = formatEnvironmentList(environments, context.defaultEnvironmentId);
      break;
    }

    case "details": {
      const envId = parsed.environmentId ?? context.defaultEnvironmentId;
      const environments = await context.api.listEnvironments();
      const env = environments.find((e: Environment) => e.name === envId);
      if (!env) return null;
      content = formatEnvironmentDetails(env);
      break;
    }

    case "summary": {
      const envId = parsed.environmentId ?? context.defaultEnvironmentId;
      const summary = await getEnvironmentSummary(context, envId);
      content = summary;
      break;
    }

    default:
      return null;
  }

  return {
    contents: [
      {
        uri,
        mimeType: "application/json",
        text: JSON.stringify(content, null, 2),
      },
    ],
  };
}

// ============================================================================
// Formatting Helpers
// ============================================================================

interface FormattedEnvironment {
  id: string;
  name: string;
  displayName: string;
  type: string;
  isDefault: boolean;
  region: string;
  createdTime: string;
  state: string;
}

function formatEnvironmentList(
  environments: Environment[],
  defaultId: string
): { environments: FormattedEnvironment[]; count: number; defaultEnvironmentId: string } {
  return {
    environments: environments.map((env) => ({
      id: env.name,
      name: env.name,
      displayName: env.properties.displayName,
      type: env.properties.environmentSku,
      isDefault: env.name === defaultId,
      region: env.properties.azureRegionHint,
      createdTime: env.properties.createdTime,
      state: env.properties.provisioningState,
    })),
    count: environments.length,
    defaultEnvironmentId: defaultId,
  };
}

function formatEnvironmentDetails(env: Environment): {
  id: string;
  displayName: string;
  type: string;
  region: string;
  state: string;
  createdTime: string;
  createdBy: { id: string | null; displayName: string | null } | null;
  lastModifiedTime: string;
  runtimeEndpoints: Record<string, string | null> | null;
} {
  // Flow environment metadata can omit these fields despite the API type.
  // Preserve unavailable metadata as null; never infer an identity or endpoint.
  const asRecord = (value: unknown): Record<string, unknown> | null =>
    value !== null && typeof value === "object" && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  const createdBy = asRecord(env.properties.createdBy);
  const runtimeEndpoints = asRecord(env.properties.runtimeEndpoints);
  return {
    id: env.name,
    displayName: env.properties.displayName,
    type: env.properties.environmentSku,
    region: env.properties.azureRegionHint,
    state: env.properties.provisioningState,
    createdTime: env.properties.createdTime,
    createdBy: createdBy ? {
      id: typeof createdBy.id === "string" ? createdBy.id : null,
      displayName: typeof createdBy.displayName === "string" ? createdBy.displayName : null,
    } : null,
    lastModifiedTime: env.properties.lastModifiedTime,
    runtimeEndpoints: runtimeEndpoints
      ? Object.fromEntries(Object.entries(runtimeEndpoints).map(([key, value]) => [
        key, typeof value === "string" ? value : null,
      ]))
      : null,
  };
}

async function getEnvironmentSummary(
  context: EnvironmentResourceContext,
  environmentId: string
): Promise<{
  environmentId: string;
  flows: { total: number; started: number; stopped: number; suspended: number };
  recentActivity: { lastFlowRun?: string; lastModified?: string };
}> {
  // Get flows for the environment
  const result = await context.api.listFlows({ environment: environmentId });
  const flows = result.flows;

  const flowStats = {
    total: flows.length,
    started: flows.filter((f) => f.properties.state === "Started").length,
    stopped: flows.filter((f) => f.properties.state === "Stopped").length,
    suspended: flows.filter((f) => f.properties.state === "Suspended").length,
  };

  // Find most recent activity
  let lastModified: string | undefined;
  for (const flow of flows) {
    if (!lastModified || flow.properties.lastModifiedTime > lastModified) {
      lastModified = flow.properties.lastModifiedTime;
    }
  }

  return {
    environmentId,
    flows: flowStats,
    recentActivity: {
      lastModified,
    },
  };
}

// ============================================================================
// URI Parsing
// ============================================================================

interface ParsedEnvironmentUri {
  type: "list" | "details" | "summary";
  environmentId?: string;
}

function parseEnvironmentUri(uri: string): ParsedEnvironmentUri | null {
  const path = uri.replace("powerautomate://", "");

  if (path === "environments" || path === "environments/") {
    return { type: "list" };
  }

  const summaryMatch = path.match(/^environments\/([^/]+)\/summary$/);
  if (summaryMatch?.[1]) {
    return { type: "summary", environmentId: summaryMatch[1] };
  }

  const detailsMatch = path.match(/^environments\/([^/]+)$/);
  if (detailsMatch?.[1]) {
    return { type: "details", environmentId: detailsMatch[1] };
  }

  return null;
}

/**
 * Check if a URI is an environment resource
 */
export function isEnvironmentResourceUri(uri: string): boolean {
  return uri.startsWith("powerautomate://environments");
}
